import React, { useEffect, useMemo, useState } from 'react';
import { ChartLine, ChevronDown, ChevronUp } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import type { ChartArtifactPayload } from '@cotera/api';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { cn } from '@cotera/client/v0/lib/utils';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { toChartJsViewModel } from '@cotera/client/v0/components/charts/chart-config';
import type { WorkflowRunStats } from '@cotera/client/app/etc/api/typed-client';
import { useWorkflowRunsModel } from './workflow-runs.model';

/** Status display order and semantic colors (matching the run status badges). */
const STATUS_SERIES: { status: string; label: string; color: string }[] = [
  { status: 'completed', label: 'Completed', color: '#10b981' },
  { status: 'failed', label: 'Failed', color: '#ef4444' },
  {
    status: 'incomplete',
    label: 'In progress',
    color: '#f59e0b',
  },
  { status: 'queued', label: 'Queued', color: '#3b82f6' },
  { status: 'cancelled', label: 'Cancelled', color: '#9ca3af' },
  { status: 'stopped', label: 'Stopped', color: '#6b7280' },
];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function formatBucketLabel(iso: string, bucket: 'hour' | 'day'): string {
  const date = new Date(iso);
  if (bucket === 'hour') {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Turns sparse (bucket, status, count) points into a line-chart payload with
 * one series per status and zero-filled buckets between the first and last
 * data point.
 */
function statsToChartPayload(
  stats: WorkflowRunStats
): ChartArtifactPayload | null {
  if (stats.points.length === 0) {
    return null;
  }
  const stepMs = stats.bucket === 'hour' ? HOUR_MS : DAY_MS;
  const bucketTimes = stats.points.map((p) =>
    new Date(p.bucketStart).getTime()
  );
  const min = Math.min(...bucketTimes);
  const max = Math.max(...bucketTimes);

  const bucketKeys: string[] = [];
  for (let t = min; t <= max; t += stepMs) {
    bucketKeys.push(new Date(t).toISOString());
  }
  const bucketIndex = new Map(bucketKeys.map((key, i) => [key, i]));

  const presentStatuses = new Set(stats.points.map((p) => p.status));
  const series = STATUS_SERIES.filter((s) => presentStatuses.has(s.status));
  const valuesByStatus = new Map(
    series.map((s) => [s.status, bucketKeys.map(() => 0)])
  );
  for (const point of stats.points) {
    const index = bucketIndex.get(new Date(point.bucketStart).toISOString());
    const values = valuesByStatus.get(point.status);
    if (index !== undefined && values !== undefined) {
      values[index] = point.count;
    }
  }

  return {
    chart_type: 'line',
    title: '',
    subtitle: null,
    data: {
      labels: bucketKeys.map((key) => formatBucketLabel(key, stats.bucket)),
      datasets: series.map((s) => ({
        label: s.label,
        values: valuesByStatus.get(s.status) ?? [],
      })),
    },
    callouts: null,
    options: { show_legend: true, show_data_labels: null, footnote: null },
  };
}

function colorForStatusLabel(label: string): string {
  return STATUS_SERIES.find((s) => s.label === label)?.color ?? '#9ca3af';
}

const RunsStatsChart: React.FC<{ stats: WorkflowRunStats | null }> = ({
  stats,
}) => {
  const viewModel = useMemo(() => {
    if (stats === null) {
      return null;
    }
    const payload = statsToChartPayload(stats);
    if (payload === null) {
      return null;
    }
    const vm = toChartJsViewModel(payload, colorForStatusLabel);
    // The shared view model draws lines with borderWidth 0 (points only);
    // status series need visible lines.
    const data = {
      ...vm.data,
      datasets: vm.data.datasets.map((ds) => ({ ...ds, borderWidth: 1.5 })),
    };
    // Counts are integers, so pin the y-axis to whole-number ticks. Chart.js
    // types the scales map as a union across scale kinds, which rejects the
    // linear-only `precision` key in a spread literal; assert back to the
    // scales map type (the y scale is linear at runtime via softChartScales).
    const scales = {
      ...vm.options.scales,
      y: {
        ...vm.options.scales?.['y'],
        ticks: {
          ...vm.options.scales?.['y']?.ticks,
          precision: 0,
        },
      },
    } as ChartOptions['scales'];
    const options: ChartOptions = { ...vm.options, scales };
    return { data, options };
  }, [stats]);

  if (stats === null) {
    return <Skeleton className="h-[180px] w-full rounded-md" />;
  }
  if (viewModel === null) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No runs match the current filters
      </p>
    );
  }
  return (
    <div style={{ height: 180 }} className="w-full min-w-0">
      <Line
        data={viewModel.data as ChartData<'line'>}
        options={viewModel.options as ChartOptions<'line'>}
      />
    </div>
  );
};

/**
 * Collapsible run-stats panel for the runs list header. Stats live in
 * `WorkflowRunsModel` as an event watchable driven by the same broadcasts and
 * in-progress poll as the runs list (debounced), so the chart tracks new runs
 * live — but nothing is fetched until the panel is first expanded.
 */
export const WorkflowRunsStatsPanel: React.FC = () => {
  const model = useWorkflowRunsModel();
  const [expanded, setExpanded] = useState(false);
  const stats = useWatchableValue(model.runStatsWatchable);

  useEffect(() => {
    model.setStatsEnabled(expanded);
    return () => model.setStatsEnabled(false);
  }, [model, expanded]);

  const Chevron = expanded ? ChevronUp : ChevronDown;
  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          'inline-flex items-center gap-1.5 h-8 px-2 -ml-2 rounded-md text-sm',
          'text-muted-foreground hover:text-foreground hover:bg-secondary',
          'transition-colors cursor-pointer',
          expanded && 'text-foreground'
        )}
      >
        <ChartLine className="h-4 w-4" />
        <span>{expanded ? 'Hide stats' : 'View stats'}</span>
        <Chevron className="h-3.5 w-3.5" />
      </button>
      {expanded ? (
        <div className="mt-2 rounded-md border border-border bg-background p-3">
          <RunsStatsChart stats={stats} />
        </div>
      ) : null}
    </div>
  );
};
